/**
 * ShareModal Component
 * Generic modal for sharing entities (data bundles, skills) with users or tenants
 * Based on BotShareModal but uses sharingService and fixed 'use' permission level
 */

import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { sharingService, type QuickShareInfo } from '@services/sharingService';
import type { ShareableEntityType } from '@services/sharingService';
import type {
  ShareableUser,
  ShareableTenant,
  EntityShare,
  ShareEntityPayload,
} from '@/types';
import '../../modules/chatbots/components/BotModal.css';
import '../../modules/chatbots/components/BotShareModal.css';

// Level-based styling for tenant hierarchy (same as AdminPage)
const levelStyles = [
  { color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)', icon: 'crown' },     // Level 0 - Root (purple)
  { color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)', icon: 'building' },  // Level 1 - Organization (blue)
  { color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)', icon: 'folder' },    // Level 2 - Department (green)
  { color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)', icon: 'users' },     // Level 3 - Team (amber)
  { color: '#ec4899', bgColor: 'rgba(236, 72, 153, 0.1)', icon: 'user' },      // Level 4+ - Sub-team (pink)
];

// Get icon SVG based on level
function getLevelIcon(level: number, color: string) {
  const icons: Record<string, JSX.Element> = {
    crown: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M2 20h20v2H2zm2-2L1 7l5 3 6-8 6 8 5-3-3 13H4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    ),
    building: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    folder: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    users: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="9" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    user: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  };
  const style = levelStyles[Math.min(level, levelStyles.length - 1)];
  return icons[style.icon] || icons.user;
}

export interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: ShareableEntityType;
  entityId: string;
  entityName: string;
  entityIds?: string[];
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName,
  entityIds,
}) => {
  // All entity IDs to share (bulk or single)
  const allEntityIds = entityIds && entityIds.length > 0 ? entityIds : [entityId];
  const isBulk = allEntityIds.length > 1;
  const { t } = useLanguage();
  const { isAdmin, isGroupAdmin, isSuperAdmin } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [shareType, setShareType] = useState<'user' | 'tenant' | 'own_tenant' | 'child_tenants'>('user');
  const [users, setUsers] = useState<ShareableUser[]>([]);
  const [tenants, setTenants] = useState<ShareableTenant[]>([]);
  const [existingShares, setExistingShares] = useState<EntityShare[]>([]);
  // False until the first listEntityShares round-trip, so the panel never claims
  // "not shared with anyone" while the answer is still in flight.
  const [sharesLoaded, setSharesLoaded] = useState(false);

  // Quick share state
  const [quickShareInfo, setQuickShareInfo] = useState<QuickShareInfo | null>(null);
  const [quickShareSubmitting] = useState(false);

  // Share type dropdown state
  const [isShareTypeDropdownOpen, setIsShareTypeDropdownOpen] = useState(false);
  const shareTypeDropdownRef = React.useRef<HTMLDivElement>(null);

  // Form state
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [canReshare, setCanReshare] = useState(false);
  const [restrictByRole, setRestrictByRole] = useState(false);
  const [visibleToRoles, setVisibleToRoles] = useState<string[] | null>(null);

  // Edit mode state
  const [editingShareId, setEditingShareId] = useState<string | null>(null);

  // Searchable dropdown state
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const userDropdownRef = React.useRef<HTMLDivElement>(null);
  const tenantDropdownRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && entityId) {
      setSharesLoaded(false);
      // The list belongs to the entity it was fetched for. Two callers keep
      // this modal MOUNTED between opens and do not reset the entity id
      // (Data Bundles, Skills), so without this the previous item's
      // collaborators stayed on screen under the new item's title for the
      // whole round trip — and a click on one of those rows sent its
      // share_id with the NEW entity's id (caught in review, 2026-09-11).
      setExistingShares([]);
      loadData();
      // Reset form
      setSelectedUserIds([]);
      setSelectedTenantIds([]);
      setCanReshare(false);
      setRestrictByRole(false);
      setVisibleToRoles(null);
      setShareType('user');
      setUserSearchQuery('');
      setTenantSearchQuery('');
      setIsUserDropdownOpen(false);
      setIsTenantDropdownOpen(false);
      setEditingShareId(null);
    }
  }, [isOpen, entityId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
      if (tenantDropdownRef.current && !tenantDropdownRef.current.contains(event.target as Node)) {
        setIsTenantDropdownOpen(false);
      }
      if (shareTypeDropdownRef.current && !shareTypeDropdownRef.current.contains(event.target as Node)) {
        setIsShareTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async () => {
    try {
      const [targets, shares, quickInfo] = await Promise.all([
        sharingService.getShareTargets(),
        sharingService.listEntityShares(entityType, entityId),
        sharingService.getQuickShareInfo(),
      ]);
      setUsers(targets.users);
      setTenants(targets.tenants);
      setExistingShares(shares);
      setQuickShareInfo(quickInfo);
      // Only a fetch that SUCCEEDED may be called loaded: on failure the
      // panel would otherwise say "not shared with anyone yet" about an item
      // whose shares it never read.
      setSharesLoaded(true);
    } catch (error) {
      console.error('Error loading share data:', error);
      message.error(t('sharing.loadError') || t('botSharing.loadError') || 'Failed to load sharing data');
    }
  };

  // Helper: run a share action for a single entity, or loop over allEntityIds for bulk
  const shareForAllEntities = async (
    action: (eid: string) => Promise<void>
  ): Promise<{ succeeded: number; failed: number }> => {
    let succeeded = 0;
    let failed = 0;
    for (const eid of allEntityIds) {
      try {
        await action(eid);
        succeeded++;
      } catch (err) {
        console.error(`Failed to share entity ${eid}:`, err);
        failed++;
      }
    }
    return { succeeded, failed };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Handle quick share types (own_tenant, child_tenants)
    if (shareType === 'own_tenant') {
      if (!quickShareInfo?.own_tenant) return;

      setSubmitting(true);
      try {
        const { succeeded, failed } = await shareForAllEntities(async (eid) => {
          await sharingService.shareEntity(entityType, eid, {
            target_tenant_id: quickShareInfo.own_tenant!.tenant_id,
            permission_level: 'use',
            can_reshare: canReshare,
            visible_to_roles: restrictByRole ? visibleToRoles : undefined,
          });
        });
        if (failed === 0) {
          message.success(
            isBulk
              ? `Shared ${succeeded} items to ${quickShareInfo.own_tenant.tenant_name}`
              : (t('sharing.shareToOwnTenantSuccess') || t('botSharing.shareToOwnTenantSuccess') || `Shared to ${quickShareInfo.own_tenant.tenant_name}`)
          );
        } else {
          message.warning(t('common.sharedFailedSome', { succeeded, failed }));
        }
        setCanReshare(false);
        loadData();
      } catch (error: any) {
        const errorMsg = error.response?.data?.error || t('sharing.failedOwnTenant');
        message.error(errorMsg);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (shareType === 'child_tenants') {
      if (!quickShareInfo?.child_tenants?.length) return;

      setSubmitting(true);
      try {
        let totalCreated = 0, totalSkipped = 0, totalFailed = 0;
        for (const eid of allEntityIds) {
          const result = await sharingService.bulkShareEntity(entityType, eid, {
            target_tenant_ids: quickShareInfo.child_tenants.map(t => t.tenant_id),
            permission_level: 'use',
            can_reshare: canReshare,
            visible_to_roles: restrictByRole ? visibleToRoles : undefined,
          });
          totalCreated += result.created.length;
          totalSkipped += result.skipped.length;
          totalFailed += result.failed.length;
        }

        if (totalCreated > 0 && totalFailed === 0) {
          message.success(
            isBulk
              ? `Shared ${allEntityIds.length} items to child tenants`
              : (t('sharing.bulkShareSuccess') || t('botSharing.bulkShareSuccess') ||
                t('counts.sharedToTenants', { count: totalCreated }) + (totalSkipped > 0 ? t('counts.alreadySharedSuffix', { count: totalSkipped }) : ''))
          );
        } else if (totalCreated > 0 && totalFailed > 0) {
          message.warning(t('sharing.sharedFailedTenants', { succeeded: totalCreated, failed: totalFailed }));
        } else if (totalSkipped > 0 && totalCreated === 0) {
          message.info(t('sharing.alreadySharedToAllChildren') || t('botSharing.alreadySharedToAllChildren') || 'Already shared to all child tenants');
        } else {
          message.error(t('sharing.shareToChildTenantsFailed'));
        }

        setCanReshare(false);
        loadData();
      } catch (error: any) {
        console.error('Bulk share error:', error);
        message.error(error.response?.data?.error || t('sharing.failedChildTenants'));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Validation for regular share types
    if (shareType === 'user' && selectedUserIds.length === 0) {
      message.error(t('sharing.userRequired') || t('botSharing.userRequired') || 'Please select a user');
      return;
    }
    if (shareType === 'tenant' && selectedTenantIds.length === 0) {
      message.error(t('sharing.tenantRequired') || t('botSharing.tenantRequired') || 'Please select a tenant');
      return;
    }

    setSubmitting(true);
    try {
      if (shareType === 'user') {
        // If editing, first remove the old share (only for single entity)
        if (editingShareId && !isBulk) {
          await sharingService.removeEntityShare(entityType, entityId, editingShareId);
        }

        if (selectedUserIds.length === 1) {
          // Single user share — apply to all entities
          const payload: ShareEntityPayload = {
            target_user_id: selectedUserIds[0],
            permission_level: 'use',
            can_reshare: canReshare,
          };
          const { succeeded, failed } = await shareForAllEntities(async (eid) => {
            await sharingService.shareEntity(entityType, eid, payload);
          });
          if (failed === 0) {
            message.success(
              isBulk
                ? `Shared ${succeeded} items successfully`
                : (editingShareId
                    ? (t('sharing.updateSuccess') || t('botSharing.updateSuccess') || 'Share updated successfully')
                    : (t('sharing.shareSuccess') || t('botSharing.shareSuccess') || 'Shared successfully'))
            );
          } else {
            message.warning(t('common.sharedFailedSome', { succeeded, failed }));
          }
        } else {
          // Multi-user share — apply to all entities
          let totalCreated = 0, totalSkipped = 0, totalFailed = 0;
          for (const eid of allEntityIds) {
            const result = await sharingService.bulkShareEntityUsers(entityType, eid, {
              target_user_ids: selectedUserIds,
              permission_level: 'use',
              can_reshare: canReshare,
            });
            totalCreated += result.created.length;
            totalSkipped += result.skipped.length;
            totalFailed += result.failed.length;
          }
          if (totalCreated > 0 && totalFailed === 0) {
            message.success(
              isBulk
                ? t('counts.sharedItemsToUsers', { count: selectedUserIds.length, items: allEntityIds.length })
                : (t('sharing.bulkShareUsersSuccess', { count: totalCreated }) ||
                  t('counts.sharedToUsers', { count: totalCreated }) + (totalSkipped > 0 ? t('counts.alreadySharedSuffix', { count: totalSkipped }) : ''))
            );
          } else if (totalCreated > 0 && totalFailed > 0) {
            message.warning(t('sharing.sharedFailedUsers', { succeeded: totalCreated, failed: totalFailed }));
          } else if (totalSkipped > 0 && totalCreated === 0) {
            message.info(t('sharing.allUsersHaveAccess'));
          } else {
            message.error(t('sharing.shareToUsersFailed'));
          }
        }
      } else {
        // Tenant share
        // If editing, first remove the old share (only for single entity)
        if (editingShareId && !isBulk) {
          await sharingService.removeEntityShare(entityType, entityId, editingShareId);
        }

        if (selectedTenantIds.length === 1) {
          // Single tenant share — apply to all entities
          const payload: ShareEntityPayload = {
            target_tenant_id: selectedTenantIds[0],
            permission_level: 'use',
            can_reshare: canReshare,
            visible_to_roles: restrictByRole ? visibleToRoles : undefined,
          };
          const { succeeded, failed } = await shareForAllEntities(async (eid) => {
            await sharingService.shareEntity(entityType, eid, payload);
          });
          if (failed === 0) {
            message.success(
              isBulk
                ? `Shared ${succeeded} items successfully`
                : (editingShareId
                    ? (t('sharing.updateSuccess') || t('botSharing.updateSuccess') || 'Share updated successfully')
                    : (t('sharing.shareSuccess') || t('botSharing.shareSuccess') || 'Shared successfully'))
            );
          } else {
            message.warning(t('common.sharedFailedSome', { succeeded, failed }));
          }
        } else {
          // Multi-tenant share — apply to all entities
          let totalCreated = 0, totalSkipped = 0, totalFailed = 0;
          for (const eid of allEntityIds) {
            const result = await sharingService.bulkShareEntity(entityType, eid, {
              target_tenant_ids: selectedTenantIds,
              permission_level: 'use',
              can_reshare: canReshare,
              visible_to_roles: restrictByRole ? visibleToRoles : undefined,
            });
            totalCreated += result.created.length;
            totalSkipped += result.skipped.length;
            totalFailed += result.failed.length;
          }
          if (totalCreated > 0 && totalFailed === 0) {
            message.success(
              isBulk
                ? t('counts.sharedItemsToTenants', { count: selectedTenantIds.length, items: allEntityIds.length })
                : (t('counts.sharedToTenants', { count: totalCreated }) + (totalSkipped > 0 ? t('counts.alreadySharedSuffix', { count: totalSkipped }) : ''))
            );
          } else if (totalCreated > 0 && totalFailed > 0) {
            message.warning(t('sharing.sharedFailedTenants', { succeeded: totalCreated, failed: totalFailed }));
          } else if (totalSkipped > 0 && totalCreated === 0) {
            message.info(t('sharing.allTenantsHaveAccess'));
          } else {
            message.error(t('sharing.shareToTenantsFailed'));
          }
        }
      }

      // Reset form and reload
      setSelectedUserIds([]);
      setSelectedTenantIds([]);
      setCanReshare(false);
      setRestrictByRole(false);
      setVisibleToRoles(null);
      setEditingShareId(null);
      loadData();
    } catch (error: any) {
      console.error('Error sharing entity:', error);
      const errorMsg = error.response?.data?.error || 'Failed to share';
      message.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    try {
      await sharingService.removeEntityShare(entityType, entityId, shareId);
      message.success(t('sharing.removeSuccess') || t('botSharing.removeSuccess') || 'Share removed');
      // If we were editing this share, exit edit mode
      if (editingShareId === shareId) {
        setEditingShareId(null);
        resetForm();
      }
      loadData();
    } catch (error) {
      console.error('Error removing share:', error);
      message.error(t('sharing.removeError') || t('botSharing.removeError') || 'Failed to remove share');
    }
  };

  // Reset form to defaults
  const resetForm = () => {
    setSelectedUserIds([]);
    setSelectedTenantIds([]);
    setCanReshare(false);
    setRestrictByRole(false);
    setVisibleToRoles(null);
    setShareType('user');
  };

  // Handle edit button click on a share
  const handleEditShare = (share: EntityShare) => {
    if (editingShareId === share.share_id) {
      // Toggle off edit mode
      setEditingShareId(null);
      resetForm();
      return;
    }

    // Enter edit mode for this share
    setEditingShareId(share.share_id);

    // Populate form with share data
    if (share.share_type === 'user') {
      setShareType('user');
      setSelectedUserIds([share.shared_with_user_id || '']);
      setSelectedTenantIds([]);
    } else {
      setShareType('tenant');
      setSelectedTenantIds([share.shared_with_tenant_id || '']);
      setSelectedUserIds([]);
    }

    setCanReshare(share.can_reshare);

    if (share.visible_to_roles && share.visible_to_roles.length > 0) {
      setRestrictByRole(true);
      setVisibleToRoles(share.visible_to_roles);
    } else {
      setRestrictByRole(false);
      setVisibleToRoles(null);
    }
  };

  const handleClose = () => {
    if (!submitting && !quickShareSubmitting) {
      onClose();
    }
  };

  // Check if already shared to own tenant
  const isSharedToOwnTenant = () => {
    if (!quickShareInfo?.own_tenant) return false;
    return existingShares.some(
      share => share.share_type === 'tenant' && share.shared_with_tenant_id === quickShareInfo.own_tenant.tenant_id
    );
  };

  // Check how many child tenants are already shared
  const getChildTenantsShareStatus = () => {
    if (!quickShareInfo?.child_tenants?.length) return { shared: 0, total: 0 };
    const sharedCount = quickShareInfo.child_tenants.filter(ct =>
      existingShares.some(share => share.share_type === 'tenant' && share.shared_with_tenant_id === ct.tenant_id)
    ).length;
    return { shared: sharedCount, total: quickShareInfo.child_tenants.length };
  };

  // Get level style for tenant depth
  const getLevelStyle = (depth: number) => {
    return levelStyles[Math.min(depth, levelStyles.length - 1)];
  };

  // Share type options for dropdown
  const getShareTypeOptions = () => {
    const options: Array<{
      id: 'user' | 'tenant' | 'own_tenant' | 'child_tenants';
      label: string;
      description: string;
      icon: JSX.Element;
      disabled?: boolean;
      badge?: string;
    }> = [
      {
        id: 'user',
        label: t('sharing.shareToUser') || t('botSharing.shareToUser') || 'Share to User',
        description: t('sharing.shareToUserDesc') || t('botSharing.shareToUserDesc') || 'Share to a specific user',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        ),
      },
      {
        id: 'tenant',
        label: t('sharing.shareToTenant') || t('botSharing.shareToTenant') || 'Share to Tenant',
        description: t('sharing.shareToTenantDesc') || t('botSharing.shareToTenantDesc') || 'Share to all users in a tenant',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        ),
      },
    ];

    // Add own tenant option for admins
    if (isAdmin && quickShareInfo?.own_tenant) {
      options.push({
        id: 'own_tenant',
        label: t('sharing.shareToOwnTenant') || t('botSharing.shareToOwnTenant') || 'Share to Own Tenant',
        description: `${quickShareInfo.own_tenant.tenant_name} - ${t('sharing.allUsersInTenant') || t('botSharing.allUsersInTenant') || 'All users'}`,
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
          </svg>
        ),
        disabled: isSharedToOwnTenant(),
        badge: isSharedToOwnTenant() ? (t('sharing.shared') || t('botSharing.shared') || 'Shared') : undefined,
      });
    }

    // Add child tenants option for group_admin and super_admin
    if ((isGroupAdmin || isSuperAdmin) && quickShareInfo?.child_tenants && quickShareInfo.child_tenants.length > 0) {
      const status = getChildTenantsShareStatus();
      options.push({
        id: 'child_tenants',
        label: t('sharing.shareToAllChildren', { count: quickShareInfo.child_tenants.length }) || t('botSharing.shareToAllChildren', { count: quickShareInfo.child_tenants.length }) || `Share to ${quickShareInfo.child_tenants.length} Child Tenant(s)`,
        description: t('sharing.shareToAllChildTenantsDesc') || t('botSharing.shareToAllChildTenantsDesc') || 'Share to all direct child tenants',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        ),
        disabled: status.shared === status.total,
        badge: status.shared > 0 ? `${status.shared}/${status.total}` : `${status.total}`,
      });
    }

    return options;
  };

  const getSelectedShareTypeOption = () => {
    return getShareTypeOptions().find(opt => opt.id === shareType);
  };

  const handleSelectShareType = (type: 'user' | 'tenant' | 'own_tenant' | 'child_tenants') => {
    setShareType(type);
    setIsShareTypeDropdownOpen(false);
    setSelectedUserIds([]);
    setSelectedTenantIds([]);
  };

  // Get selected user display names (utility for external use)
  // @ts-ignore unused but kept for API consistency
  const getSelectedUserNames = () => {
    return selectedUserIds.map(id => {
      const user = users.find(u => u.user_id === id);
      return user?.user_full_name || user?.user_email || '';
    }).filter(Boolean);
  };

  // Filter users by search query
  const filteredUsers = users.filter(user => {
    const query = userSearchQuery.toLowerCase();
    return (
      (user.user_full_name?.toLowerCase().includes(query) || false) ||
      user.user_email.toLowerCase().includes(query) ||
      (user.tenant_name?.toLowerCase().includes(query) || false)
    );
  });

  // Filter and sort tenants by search query, sorted by depth for hierarchy display
  const filteredTenants = tenants
    .filter(tenant => {
      const query = tenantSearchQuery.toLowerCase();
      return tenant.tenant_name.toLowerCase().includes(query) ||
             tenant.hierarchy_path.toLowerCase().includes(query);
    })
    .sort((a, b) => a.depth - b.depth);

  // Group filtered users by tenant for better UX
  const usersByTenant = filteredUsers.reduce((acc, user) => {
    const tenantName = user.tenant_name || t('sharing.unknownTenant');
    if (!acc[tenantName]) {
      acc[tenantName] = [];
    }
    acc[tenantName].push(user);
    return acc;
  }, {} as Record<string, ShareableUser[]>);

  const handleSelectUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
    // Keep dropdown open for multi-select
  };

  const handleSelectTenant = (tenantId: string) => {
    setSelectedTenantIds(prev =>
      prev.includes(tenantId)
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
    // Keep dropdown open for multi-select
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-content">
            <h2 className="modal-title">
              <svg className="share-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              {t('sharing.title') || t('botSharing.title') || 'Share'}: {entityName}
            </h2>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={handleClose}
            aria-label={t('common.close')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
            {/* Share Type Selection - Beautiful Dropdown */}
            <div className="form-group">
              <label className="form-label">
                {t('sharing.shareType') || t('botSharing.shareType') || 'Share Type'} <span className="required-indicator">*</span>
              </label>
              <div className="share-type-dropdown" ref={shareTypeDropdownRef}>
                <button
                  type="button"
                  className={`share-type-trigger ${isShareTypeDropdownOpen ? 'open' : ''} ${editingShareId ? 'disabled-edit-mode' : ''}`}
                  onClick={() => !editingShareId && setIsShareTypeDropdownOpen(!isShareTypeDropdownOpen)}
                  disabled={submitting || quickShareSubmitting || !!editingShareId}
                >
                  <div className="share-type-trigger-content">
                    <div className="share-type-trigger-icon">
                      {getSelectedShareTypeOption()?.icon}
                    </div>
                    <div className="share-type-trigger-text">
                      <span className="share-type-trigger-label">{getSelectedShareTypeOption()?.label}</span>
                      <span dir="auto" className="share-type-trigger-desc">{getSelectedShareTypeOption()?.description}</span>
                    </div>
                  </div>
                  <svg className="share-type-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6"/>
                  </svg>
                </button>
                {isShareTypeDropdownOpen && (
                  <div className="share-type-options">
                    {getShareTypeOptions().map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={`share-type-option ${shareType === option.id ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}`}
                        onClick={() => !option.disabled && handleSelectShareType(option.id)}
                        disabled={option.disabled}
                      >
                        <div className="share-type-option-icon">
                          {option.icon}
                        </div>
                        <div className="share-type-option-content">
                          <span className="share-type-option-label" dir="auto">{option.label}</span>
                          <span className="share-type-option-desc" dir="auto">{option.description}</span>
                        </div>
                        {option.badge && (
                          <span className={`share-type-badge ${option.disabled ? 'completed' : ''}`} dir="auto">
                            {option.badge}
                          </span>
                        )}
                        {shareType === option.id && (
                          <svg className="share-type-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M20 6L9 17l-5-5"/>
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* User Selection - Only show for user type */}
            {shareType === 'user' && (
              <div className="form-group">
                <label className="form-label">
                  {t('sharing.selectUser') || t('botSharing.selectUser') || 'Select User'} <span className="required-indicator">*</span>
                </label>
                <div className="searchable-select" ref={userDropdownRef}>
                  <button
                    type="button"
                    className={`select-trigger ${selectedUserIds.length > 0 ? 'has-value' : ''} ${isUserDropdownOpen ? 'open' : ''} ${editingShareId ? 'disabled-edit-mode' : ''}`}
                    onClick={() => !submitting && !editingShareId && setIsUserDropdownOpen(!isUserDropdownOpen)}
                    disabled={submitting || !!editingShareId}
                  >
                    <span dir="auto" className="select-value">
                      {selectedUserIds.length === 0
                        ? (t('sharing.searchUsers') || 'Search for users...')
                        : (t('sharing.selectedUsers', { count: selectedUserIds.length }) || `${selectedUserIds.length} user(s) selected`)}
                    </span>
                    <svg className="select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                  {isUserDropdownOpen && (
                    <div className="select-dropdown">
                      <div className="select-search">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="M21 21l-4.35-4.35"/>
                        </svg>
                        <input dir="auto"
                          type="text"
                          placeholder={t('sharing.searchUser') || t('botSharing.searchUser') || 'Search users...'}
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="select-options">
                        {Object.entries(usersByTenant).length === 0 ? (
                          <div className="select-no-results">
                            {t('common.noResults') || 'No users found'}
                          </div>
                        ) : (
                          Object.entries(usersByTenant).map(([tenantName, tenantUsers]) => (
                            <div key={tenantName} className="select-group">
                              <div className="select-group-label">{tenantName}</div>
                              {tenantUsers.map((user) => (
                                <button
                                  key={user.user_id}
                                  type="button"
                                  className={`select-option ${selectedUserIds.includes(user.user_id) ? 'selected' : ''}`}
                                  onClick={() => handleSelectUser(user.user_id)}
                                >
                                  <div className={`option-checkbox ${selectedUserIds.includes(user.user_id) ? 'checked' : ''}`}>
                                    {selectedUserIds.includes(user.user_id) && (
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M20 6L9 17l-5-5"/>
                                      </svg>
                                    )}
                                  </div>
                                  <div className="option-avatar">
                                    {(user.user_full_name || user.user_email).charAt(0).toUpperCase()}
                                  </div>
                                  <div className="option-info">
                                    <span dir="auto" className="option-name">
                                      {user.user_full_name || user.user_email}
                                      {user.tenant_name && (
                                        <span className="user-tenant-tag" dir="auto">{user.tenant_name}</span>
                                      )}
                                    </span>
                                    <span dir="auto" className="option-meta">{user.user_email} · {user.user_role}</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {selectedUserIds.length > 0 && (
                  <div className="selected-users-tags">
                    {selectedUserIds.map(uid => {
                      const user = users.find(u => u.user_id === uid);
                      return (
                        <span key={uid} className="selected-user-tag">
                          <span dir="auto" className="selected-user-tag-name">
                            {user?.user_full_name || user?.user_email || uid}
                            {user?.tenant_name && (
                              <span className="user-tenant-tag" dir="auto">{user.tenant_name}</span>
                            )}
                          </span>
                          <button
                            type="button"
                            className="selected-user-tag-remove"
                            onClick={() => setSelectedUserIds(prev => prev.filter(id => id !== uid))}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Tenant Selection - Only show for tenant type */}
            {shareType === 'tenant' && (
              <div className="form-group">
                <label className="form-label">
                  {t('sharing.selectTenant') || t('botSharing.selectTenant') || 'Select Tenant'} <span className="required-indicator">*</span>
                </label>
                <div className="searchable-select" ref={tenantDropdownRef}>
                  <button
                    type="button"
                    className={`select-trigger ${selectedTenantIds.length > 0 ? 'has-value' : ''} ${isTenantDropdownOpen ? 'open' : ''} ${editingShareId ? 'disabled-edit-mode' : ''}`}
                    onClick={() => !submitting && !editingShareId && setIsTenantDropdownOpen(!isTenantDropdownOpen)}
                    disabled={submitting || !!editingShareId}
                  >
                    <span dir="auto" className="select-value">
                      {selectedTenantIds.length === 0
                        ? (t('sharing.searchTenant') || t('botSharing.searchTenant') || 'Search for tenants...')
                        : `${selectedTenantIds.length} tenant(s) selected`}
                    </span>
                    <svg className="select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                  {isTenantDropdownOpen && (
                    <div className="select-dropdown">
                      <div className="select-search">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="M21 21l-4.35-4.35"/>
                        </svg>
                        <input dir="auto"
                          type="text"
                          placeholder={t('sharing.searchTenant') || t('botSharing.searchTenant') || 'Search tenants...'}
                          value={tenantSearchQuery}
                          onChange={(e) => setTenantSearchQuery(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="select-options">
                        {filteredTenants.length === 0 ? (
                          <div className="select-no-results">
                            {tenants.length === 0
                              ? (t('sharing.noTenants') || t('botSharing.noTenants') || 'No tenants available')
                              : (t('common.noResults') || 'No tenants found')}
                          </div>
                        ) : (
                          filteredTenants.map((tenant) => {
                            const style = getLevelStyle(tenant.depth);
                            return (
                              <button
                                key={tenant.tenant_id}
                                type="button"
                                className={`select-option ${selectedTenantIds.includes(tenant.tenant_id) ? 'selected' : ''}`}
                                onClick={() => handleSelectTenant(tenant.tenant_id)}
                              >
                                <div className={`option-checkbox ${selectedTenantIds.includes(tenant.tenant_id) ? 'checked' : ''}`}>
                                  {selectedTenantIds.includes(tenant.tenant_id) && (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <path d="M20 6L9 17l-5-5"/>
                                    </svg>
                                  )}
                                </div>
                                <div className="option-avatar tenant" style={{ background: style.bgColor, border: 'none' }}>
                                  {getLevelIcon(tenant.depth, style.color)}
                                </div>
                                <div className="option-info">
                                  <span dir="auto" className="option-name">
                                    {tenant.tenant_name}
                                    <span className="option-badge" style={{ background: style.bgColor, color: style.color }} dir="auto">
                                      {tenant.is_root_tenant ? 'ROOT' : `Level ${tenant.depth}`}
                                    </span>
                                  </span>
                                  <span className="option-meta" dir="auto">
                                    {tenant.hierarchy_path}
                                  </span>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {selectedTenantIds.length > 0 && (
                  <div className="selected-users-tags">
                    {selectedTenantIds.map(tid => {
                      const tenant = tenants.find(t2 => t2.tenant_id === tid);
                      const style = tenant ? getLevelStyle(tenant.depth) : levelStyles[0];
                      return (
                        <span key={tid} className="selected-user-tag" style={{ borderColor: style.color }}>
                          <span dir="auto" className="selected-user-tag-name">{tenant?.tenant_name || tid}</span>
                          <button
                            type="button"
                            className="selected-user-tag-remove"
                            onClick={() => setSelectedTenantIds(prev => prev.filter(id => id !== tid))}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Role Restriction - Only for tenant share types */}
            {(shareType === 'tenant' || shareType === 'own_tenant' || shareType === 'child_tenants') && (
              <div className="form-group">
                <div className="role-restrict-toggle">
                  <label className="share-toggle-container">
                    <input
                      type="checkbox"
                      checked={restrictByRole}
                      onChange={(e) => {
                        setRestrictByRole(e.target.checked);
                        if (!e.target.checked) {
                          setVisibleToRoles(null);
                        } else {
                          setVisibleToRoles(['super_admin', 'group_admin', 'admin', 'user']);
                        }
                      }}
                      disabled={submitting}
                      className="share-toggle-input"
                    />
                    <span className="share-toggle-slider"></span>
                    <span className="share-toggle-label">{t('sharing.restrictByRole') || 'Restrict visibility by role'}</span>
                  </label>
                </div>
                {restrictByRole && (
                  <div className="role-checkboxes">
                    {(['super_admin', 'group_admin', 'admin', 'user'] as const).map(role => (
                      <label key={role} className={`role-checkbox-label ${visibleToRoles?.includes(role) ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={visibleToRoles?.includes(role) ?? false}
                          onChange={(e) => {
                            setVisibleToRoles(prev => {
                              const current = prev || [];
                              return e.target.checked
                                ? [...current, role]
                                : current.filter(r => r !== role);
                            });
                          }}
                          className="role-checkbox-input"
                        />
                        <span className="role-checkbox-check">
                          {visibleToRoles?.includes(role) && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <path d="M20 6L9 17l-5-5"/>
                            </svg>
                          )}
                        </span>
                        <span className="role-checkbox-name">{role.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Allow Resharing Toggle */}
            <div className="toggle-row">
              <label className="share-toggle-container">
                <input
                  type="checkbox"
                  checked={canReshare}
                  onChange={(e) => setCanReshare(e.target.checked)}
                  disabled={submitting}
                  className="share-toggle-input"
                />
                <span className="share-toggle-slider"></span>
                <span className="share-toggle-label">{t('sharing.allowReshare') || t('botSharing.allowReshare') || 'Allow Resharing'}</span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={handleClose}
                disabled={submitting}
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button type="submit" className={`btn-primary ${editingShareId ? 'btn-edit-mode' : ''}`} disabled={submitting}>
                {submitting ? (
                  <span className="loading-indicator">
                    <div className="loading-spinner"></div>
                    <span>{editingShareId ? (t('common.updating') || 'Updating...') : (t('common.sharing') || 'Sharing...')}</span>
                  </span>
                ) : (
                  <span>{editingShareId ? (t('common.accept') || 'Accept') : (t('sharing.share') || t('botSharing.share') || 'Share')}</span>
                )}
              </button>
            </div>
          </form>

        {/* Existing Shares — hidden in bulk mode since shares differ per entity */}
        {/* Existing Shares — always rendered at the same height so the modal
            keeps one shape whether the item is shared with nobody or with
            twenty people; the list scrolls inside the panel. */}
        {!isBulk && (
          <div className="existing-shares-section">
            <div className="existing-shares-title">
              {t('sharing.existingShares') || t('botSharing.existingShares') || 'Current Shares'}
              {sharesLoaded ? (
                <span className="existing-shares-count">{existingShares.length}</span>
              ) : (
                <div className="loading-spinner small"></div>
              )}
            </div>
            <div className="shares-list">
              {sharesLoaded && existingShares.length === 0 && (
                <div className="shares-empty">
                  {t('sharing.noShares') || t('botSharing.noShares') || 'Not shared with anyone yet'}
                </div>
              )}
              {existingShares.map((share) => (
                <div key={share.share_id} className={`share-item ${editingShareId === share.share_id ? 'editing' : ''}`}>
                  <div className="share-item-info">
                    <div className="share-item-icon">
                      {share.share_type === 'user' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                          <circle cx="12" cy="7" r="4"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                      )}
                    </div>
                    <div className="share-item-details">
                      <span dir="auto" className="share-item-name">
                        {share.share_type === 'user'
                          ? (share.shared_with_user_name || share.shared_with_user_email)
                          : share.shared_with_tenant_name}
                      </span>
                      <div className="share-item-badges">
                        <span className={`share-badge ${share.permission_level}`} dir="auto">
                          {share.permission_level}
                        </span>
                        {share.can_reshare && (
                          <span className="share-badge reshare">{t('sharing.canReshare')}</span>
                        )}
                        {share.visible_to_roles && share.visible_to_roles.length > 0 && (
                          <span className="share-badge roles">
                            {share.visible_to_roles.map(r => r.replace(/_/g, ' ')).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="share-item-actions">
                    <button
                      type="button"
                      className={`share-edit-btn ${editingShareId === share.share_id ? 'active' : ''}`}
                      onClick={() => handleEditShare(share)}
                      title={editingShareId === share.share_id ? (t('common.cancelEdit') || 'Cancel edit') : (t('common.edit') || 'Edit')}
                    >
                      {editingShareId === share.share_id ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      className="share-remove-btn"
                      onClick={() => handleRemoveShare(share.share_id)}
                      title={t('common.remove') || 'Remove share'}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareModal;
